import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  Pressable,
  Share,
  TextInput,
  View,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { MyCatalogServicesCard } from '@/components/profile/my-catalog-services-card';
import { PageLayout } from '@/components/ui/page-layout';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useUploadAvatar } from '@/hooks/use-avatar';
import { useMyPublicProfile } from '@/hooks/use-public-profile';
import { useProviderProfile, useUpdateProvider } from '@/hooks/use-provider';
import { usePortfolioPhotos } from '@/hooks/use-provider-portfolio';
import { useServiceCatalog } from '@/hooks/use-service-catalog';
import { useTerms } from '@/hooks/use-terms';
import { useActingProviderStore } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';

const PUBLIC_BASE_URL = 'https://controlapag.com.br';

/**
 * O profissional de uma equipe não tem vitrine própria: ele aparece dentro da
 * página da organização. Aqui ele cuida do que é dele — foto, nome, bio,
 * trabalhos — e vê o link do negócio, que é o que leva cliente até ele.
 * A marca (logo, capa, cor, horário, descoberta, seções) é do dono e nem
 * aparece; a API recusa de qualquer jeito (`OWNER_ONLY`).
 */
export function TeamMemberProfileScreen() {
  const { addToast } = useToastStore();
  const { user } = useAuthStore();
  const terms = useTerms();
  /**
   * O dono pode abrir esta tela com um profissional em foco: nome e bio a API
   * resolve pelo header, mas a foto sai por `POST /user/avatar`, que é da
   * conta logada. Sob foco ela é só de leitura.
   */
  const acting = useActingProviderStore((s) => s.acting);
  const isActing = !!acting;
  const avatarImage = isActing ? acting.image : (user?.image ?? null);

  const { data: profile, isLoading } = useMyPublicProfile();
  const { data: provider } = useProviderProfile();
  const { data: photos } = usePortfolioPhotos();
  const { data: catalog } = useServiceCatalog();
  const updateProvider = useUpdateProvider();
  const uploadAvatar = useUploadAvatar();

  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!provider || hydratedRef.current) return;
    hydratedRef.current = true;
    setName(provider.title ?? '');
    setBio(provider.bio ?? '');
  }, [provider]);

  const organization = profile?.organization ?? null;
  const publicUrl = organization?.slug ? `${PUBLIC_BASE_URL}/@${organization.slug}` : null;
  // O link é o da página dele dentro da loja.
  const myUrl = publicUrl ? `${publicUrl}/professional/${organization?.providerId}` : null;

  const mine = (catalog ?? []).filter((item) => item.mine);

  const dirty =
    !!provider && (name.trim() !== (provider.title ?? '') || bio.trim() !== (provider.bio ?? ''));

  const handleSave = () => {
    if (!name.trim()) {
      addToast('Escreva como você quer aparecer.', 'error');
      return;
    }
    updateProvider.mutate(
      { title: name.trim(), bio: bio.trim() },
      {
        onSuccess: () => addToast('Perfil atualizado!', 'success'),
        onError: (error) =>
          addToast(getApiErrorMessage(error, 'Não foi possível salvar o perfil.'), 'error'),
      },
    );
  };

  const pickAvatar = async () => {
    if (isActing) {
      addToast(`A foto de ${acting.name} é ele mesmo quem troca.`, 'error');
      return;
    }
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permissão para acessar fotos é necessária.', 'error');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (result.canceled || !result.assets[0]) return;

    uploadAvatar.mutate(result.assets[0].uri, {
      onSuccess: () => addToast('Foto atualizada!', 'success'),
      onError: (error) =>
        addToast(getApiErrorMessage(error, 'Não foi possível enviar a foto.'), 'error'),
    });
  };

  const handleShare = async () => {
    if (!myUrl) return;
    try {
      if (Platform.OS === 'web') {
        const nav = globalThis.navigator as Navigator | undefined;
        if (nav?.share) {
          await nav.share({ url: myUrl });
        } else {
          await nav?.clipboard?.writeText(myUrl);
          addToast('Link copiado!', 'success');
        }
        return;
      }
      await Share.share({ message: `${myUrl}?utm_source=profile_share` });
    } catch {
      // usuário cancelou o share sheet
    }
  };

  const handlePreview = () => {
    if (!organization?.slug) return;
    const path = `/@${organization.slug}/professional/${organization.providerId}`;
    if (Platform.OS === 'web') {
      void Linking.openURL(path);
      return;
    }
    router.push(path as never);
  };

  if (isLoading || !profile) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <PageLayout
      title={isActing ? `Perfil de ${acting.name}` : 'Seu perfil'}
      subtitle={
        isActing
          ? `Como ${acting.name} aparece na página de ${organization?.name ?? terms.organization}`
          : `Como você aparece na página de ${organization?.name ?? terms.organization}`
      }
      onBack={() => router.back()}
    >
      <View className="gap-4">
        {/* Onde o cliente te encontra: a página é do negócio, não sua. */}
        <Card>
          <CardContent className="gap-3 py-4">
            <View className="flex-row items-center gap-2">
              <Ionicons className="text-neutral-500" name="storefront-outline" size={18} />
              <ThemedText className="font-semibold">
                {isActing ? acting.name : 'Você'} atende por{' '}
                {organization?.name ?? terms.organization}
              </ThemedText>
            </View>
            {publicUrl ? (
              <>
                <ThemedText className="text-foreground/60 text-sm">
                  O link é o do negócio. O seu abre a página já com você escolhido.
                </ThemedText>
                <ThemedText className="font-medium text-sm text-primary">{publicUrl}</ThemedText>
                <View className="flex-row flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="grow basis-40"
                    title="Compartilhar"
                    onPress={() => void handleShare()}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="grow basis-40"
                    title="Ver como cliente"
                    onPress={handlePreview}
                  />
                </View>
              </>
            ) : (
              <ThemedText className="text-foreground/60 text-sm">
                A página ainda não está publicada. Quando o dono publicar, o link aparece aqui.
              </ThemedText>
            )}
          </CardContent>
        </Card>

        {/* Kit de divulgação: o QR e o post levam para a página dele na loja. */}
        {myUrl && (
          <Card>
            <CardContent className="gap-3 py-4">
              <ThemedText className="font-semibold">Divulgação</ThemedText>
              <ThemedText className="text-foreground/60 text-sm">
                Leve cliente direto para a sua página dentro de{' '}
                {organization?.name ?? terms.organization}.
              </ThemedText>
              <Button
                variant="outline"
                title="QR code do perfil"
                onPress={() => router.push('/(tabs)/(provider)/profile/qr-code')}
              />
              <Button
                variant="outline"
                title="Criar post para redes sociais"
                onPress={() => router.push('/(tabs)/(provider)/profile/post-generator')}
              />
            </CardContent>
          </Card>
        )}

        {/* Foto, nome e bio: o que é seu na página da equipe. */}
        <Card>
          <CardContent className="gap-4 py-4">
            <ThemedText className="font-semibold">Suas informações</ThemedText>

            <View className="flex-row items-center gap-4">
              <Pressable
                onPress={() => void pickAvatar()}
                disabled={isActing}
                className="items-center gap-1"
              >
                {avatarImage ? (
                  <Image
                    source={{ uri: avatarImage }}
                    aria-hidden
                    style={{ width: 72, height: 72, borderRadius: 36 }}
                  />
                ) : (
                  <View className="h-[72px] w-[72px] items-center justify-center rounded-full border border-dashed border-border">
                    <Ionicons className="text-neutral-400" name="person-outline" size={22} />
                  </View>
                )}
                {!isActing && (
                  <ThemedText className="text-foreground/60 text-xs">
                    {uploadAvatar.isPending ? 'Enviando...' : 'Trocar foto'}
                  </ThemedText>
                )}
              </Pressable>

              <ThemedText className="text-foreground/60 flex-1 text-sm">
                É essa foto que o cliente vê ao escolher com quem marcar.
              </ThemedText>
            </View>

            <View className="gap-1">
              <ThemedText className="font-medium text-sm">Nome</ThemedText>
              <TextInput
                value={name}
                onChangeText={setName}
                placeholder="Como você quer aparecer"
                placeholderTextColor="#999"
                maxLength={80}
                className="rounded-lg border border-border bg-background px-3 py-2 text-foreground"
              />
            </View>

            <View className="gap-1">
              <ThemedText className="font-medium text-sm">Sobre você</ThemedText>
              <TextInput
                value={bio}
                onChangeText={setBio}
                placeholder="Ex.: 10 anos de barbearia, especialista em degradê."
                placeholderTextColor="#999"
                multiline
                numberOfLines={4}
                maxLength={500}
                textAlignVertical="top"
                className="min-h-[96px] rounded-lg border border-border bg-background px-3 py-2 text-foreground"
              />
            </View>

            <Button
              title={updateProvider.isPending ? 'Salvando...' : 'Salvar'}
              disabled={!dirty || updateProvider.isPending}
              onPress={handleSave}
            />
          </CardContent>
        </Card>

        {/* Galeria: as fotos entram na página da equipe marcadas como suas. */}
        <Card>
          <CardContent className="gap-3 py-4">
            <ThemedText className="font-semibold">Seus trabalhos</ThemedText>
            <ThemedText className="text-foreground/60 text-sm">
              {photos?.length
                ? `${photos.length} ${photos.length === 1 ? 'foto sua' : 'fotos suas'} na galeria da página.`
                : 'Suas fotos entram na galeria da página, marcadas como suas.'}
            </ThemedText>
            <Button
              variant="outline"
              title="Gerenciar fotos da galeria"
              onPress={() => router.push('/(tabs)/(provider)/profile/gallery')}
            />
          </CardContent>
        </Card>

        {/* O que você faz: o catálogo é do negócio, o preço é seu. */}
        <MyCatalogServicesCard items={mine} />
      </View>
    </PageLayout>
  );
}
